import React, { useEffect, useId, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import "./Modal.css";

// Module-level scroll lock counter (shared across all Modal instances)
let scrollLockCount = 0;

/**
 * Selector for elements that can receive focus inside the modal.
 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * A reusable modal component
 * Provides a backdrop and content container with accessibility features
 */
const Modal = ({ isOpen, onClose, title, children, size = "medium" }) => {
  const modalTitleId = useId();
  const contentRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  // Focus trap — keep Tab cycling within the modal
  const handleKeyDown = useCallback((e) => {
    if (e.key !== "Tab" || !contentRef.current) return;

    const focusableEls =
      contentRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
    if (focusableEls.length === 0) return;

    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    if (e.shiftKey) {
      // Shift+Tab — wrap from first to last
      if (document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      }
    } else {
      // Tab — wrap from last to first
      if (document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
  }, []);

  // Move focus into modal on open, restore on close
  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement;

    // Slight delay to let React render children before focusing
    const id = requestAnimationFrame(() => {
      if (contentRef.current) {
        const firstFocusable =
          contentRef.current.querySelector(FOCUSABLE_SELECTOR);
        if (firstFocusable) {
          firstFocusable.focus();
        } else {
          // Fallback: make the content div itself focusable
          contentRef.current.focus();
        }
      }
    });

    return () => {
      cancelAnimationFrame(id);
      // Restore focus to the element that triggered the modal
      if (
        previousFocusRef.current &&
        typeof previousFocusRef.current.focus === "function"
      ) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen]);

  // Prevent body scroll when modal is open (ref-counted for nested modals)
  useEffect(() => {
    if (!isOpen) return;

    scrollLockCount++;
    document.body.style.overflow = "hidden";

    return () => {
      scrollLockCount--;
      if (scrollLockCount <= 0) {
        scrollLockCount = 0;
        document.body.style.overflow = "unset";
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="modal-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
      onKeyDown={handleKeyDown}
    >
      <div
        className={`modal-content modal-${size}`}
        ref={contentRef}
        tabIndex={-1}
      >
        <div className="modal-header">
          <h2 id={modalTitleId} className="modal-title">
            {title}
          </h2>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            &#x2715;
          </button>
        </div>

        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(["small", "medium", "large"]),
};

export default Modal;
